import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import { getLuxonLocale } from '@/lib/utils';

/**
 * Analytics chart axis helpers. Charts render one row per backend bucket (`points[]` or
 * `sales` keys). We sort by `date` client-side; sparse responses (missing empty buckets)
 * produce shorter series—no synthetic zero-fill unless product adds it later.
 */

/** Granularity used for chart bucket labels (matches analytics API). */
export type ChartGranularity = 'minute' | 'hour' | 'day' | 'week' | 'month';

/**
 * Format a bucket instant for the X-axis. Uses the same locale rules as the previous
 * chart implementations (week boundaries depend on locale).
 */
export function formatChartAxisLabel(
  date: DateTime,
  granularity: ChartGranularity,
  appLocale: Locales
): string {
  const dateLocale = date.setLocale(getLuxonLocale(appLocale));
  switch (granularity) {
    case 'minute':
      return date.toFormat('HH:mm');
    case 'hour':
      return dateLocale.toLocaleString(DateTime.TIME_SIMPLE);
    case 'day':
      return dateLocale.toLocaleString({ month: 'short', day: '2-digit' });
    case 'week':
      return `${dateLocale.startOf('week').toLocaleString({ month: 'short', day: '2-digit' })} - ${dateLocale
        .endOf('week')
        .toLocaleString({ month: 'short', day: '2-digit' })}`;
    case 'month':
      return dateLocale.toLocaleString({ month: 'short', year: 'numeric' });
    default:
      return date.toFormat('dd LLL yyyy');
  }
}

/** Sort ISO date strings chronologically (invalid dates last). */
export function compareIsoDateStrings(a: string, b: string): number {
  const ta = DateTime.fromISO(a).toMillis();
  const tb = DateTime.fromISO(b).toMillis();
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return ta - tb;
}

/** Canonical bucket start ISO for merging duplicate API keys into one bar/point. */
export function bucketStartIso(
  dateIso: string,
  granularity: ChartGranularity,
  appLocale: Locales
): string | null {
  const dt = DateTime.fromISO(dateIso);
  if (!dt.isValid) return null;
  return (
    dt.setLocale(getLuxonLocale(appLocale)).startOf(granularity).toISO() ?? null
  );
}

/**
 * Y domain of the cumulative downloads chart (ANLT-06): starts at the round
 * multiple (0,5 × a power of 10) at or below 97% of the smallest value, so
 * the curve shows; ends on the first multiple of that step at or above the
 * largest value. Below 1 (no downloads yet) the axis starts at 0.
 */
export function cumulativeAxisDomain(
  min: number,
  max: number
): [number, number] {
  // toPrecision drops float noise (5154.639… × 0,97 = 5000, not 4999,99…).
  const target = Number((min * 0.97).toPrecision(12));
  if (!(target >= 1)) return [0, Math.max(max, 1)];
  const step = 0.5 * 10 ** Math.floor(Math.log10(target));
  const start = Math.floor(target / step) * step;
  const end = start + Math.max(1, Math.ceil((max - start) / step)) * step;
  return [start, end];
}
